import type { XmaxTelemetryParams } from '../telemetry/collector'

export type RemoteFailure = 'remote_output_stalled' | 'remote_stats_timeout' | 'remote_unpublished' | 'remote_user_left' | 'rtc_reconnect_timeout'
export type RemoteStallInfo = { userId: string; stallMs: number; reason?: RemoteFailure; diagnostics?: XmaxTelemetryParams }

/** Independent of player DOM; never treats absent fields as zero samples. */
export class RemoteQualityMonitor {
  private userId?: string
  private lastSampleAt?: number
  private zeroSince?: number
  private missingSince?: number
  private missingReason?: RemoteFailure
  private last: XmaxTelemetryParams = {}
  private reported?: RemoteFailure
  private lastPresentedAt?: number

  reset(): void {
    this.userId = undefined
    this.lastSampleAt = this.zeroSince = this.missingSince = undefined
    this.missingReason = undefined
    this.last = {}
    this.reported = undefined
    this.lastPresentedAt = undefined
  }

  presented(now: number): void { this.lastPresentedAt = now }

  sample(userId: string, stats: Record<string, unknown>, now: number): void {
    if (this.userId !== userId) { this.reset(); this.userId = userId }
    const bitrate = stats.receivedKBitrate
    const decoded = stats.decoderOutputFrameRate
    const received = stats.receivedFrameRate
    this.lastSampleAt = now
    this.last = {
      rtc_reason_code: this.last.rtc_reason_code,
      down_bitrate_kbps: typeof bitrate === 'number' ? bitrate : undefined,
      down_decode_fps: typeof decoded === 'number' ? decoded : undefined,
      down_receive_fps: typeof received === 'number' ? received : undefined,
      down_render_fps: typeof stats.rendererOutputFrameRate === 'number' ? stats.rendererOutputFrameRate : undefined,
    }
    if ((typeof bitrate === 'number' && bitrate > 0) || (typeof decoded === 'number' && decoded > 0)) {
      this.zeroSince = this.missingSince = undefined
      this.missingReason = undefined
      this.reported = undefined
      this.last.rtc_reason_code = undefined
    } else if (bitrate === 0 && decoded === 0) {
      this.zeroSince ??= now
    } else {
      this.zeroSince = undefined
    }
  }

  missing(userId: string, reason: 'remote_unpublished' | 'remote_user_left', now: number, rtcReason?: number): void {
    if (this.userId !== userId) { this.reset(); this.userId = userId }
    this.missingSince ??= now
    this.missingReason = reason
    this.last.rtc_reason_code = rtcReason
  }

  suspend(now: number): void {
    this.zeroSince = undefined
    if (this.lastSampleAt !== undefined) this.lastSampleAt = now
    if (this.missingSince !== undefined) this.missingSince = now
  }

  check(now: number): RemoteStallInfo | undefined {
    if (!this.userId) return
    const since = this.missingSince ?? this.zeroSince ?? this.lastSampleAt
    if (since === undefined || now - since < 20_000) return
    if (this.lastPresentedAt !== undefined && now - this.lastPresentedAt < 20_000) return
    const fresh = this.lastSampleAt !== undefined && now - this.lastSampleAt <= 5_000
    const reason = this.missingReason ?? (fresh && this.zeroSince !== undefined ? 'remote_output_stalled' : 'remote_stats_timeout')
    if (this.reported === reason) return
    this.reported = reason
    return {
      userId: this.userId, stallMs: Math.round(now - since), reason,
      diagnostics: { ...this.last, detection_source: this.missingReason ? 'rtc_event' : 'rtc_stats', stats_age_ms: this.lastSampleAt === undefined ? undefined : Math.round(now - this.lastSampleAt) },
    }
  }
}
